import React, { useMemo, useState } from 'react'
import {
  Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Link, TextField, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { useSnackbar } from 'notistack'
import { useGetAllCourierPartnersCachedQuery } from '../../../redux/commonApiCalls'
import { C, SX, TYPE } from '../../merchants/shared/tokens'
import { errorText, uploadRateCard } from '../ptlSetupApi'
import { num } from '../constants'
import { Banner } from '../components/SetupUi'
import KpiStrip from '../shell/KpiStrip'
import UploadErrors from './UploadErrors'

/** Live courier accounts; the PTL ones (supported_shipment B2B) are offered. The server checks the pick again. */
const COURIER_FILTER = {
  andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
  offset: 0,
  limit: 1000,
  skipCount: true,
  includes: 'id,name,cpAccountCode,prozoId,supportedShipmentType',
}

/**
 * Upload of one courier's PTL rate card for one merchant, D2C commit pattern: choose courier + file → check (nothing is
 * saved; whole file, errors with row numbers) → preview → confirm dialog → save. The same file is sent twice, so what
 * is saved is exactly what was checked.
 */
export default function RateCardUpload({ merchant, presetCourier, existingCouriers, standardLanes, onTemplate, onCancel, onSaved }) {
  const { enqueueSnackbar } = useSnackbar()
  const { data: couriers = [], isLoading } = useGetAllCourierPartnersCachedQuery(COURIER_FILTER)
  // A rate card is keyed by prozoId, and some prozoIds have more than one account, so each prozoId is offered once.
  const options = useMemo(() => {
    const byId = new Map()
    for (const c of couriers || []) {
      if (c.supported_shipment !== 'B2B' || !c.prozoId || byId.has(c.prozoId)) continue
      byId.set(c.prozoId, { value: c.prozoId, label: c.cpAccountCode && c.cpAccountCode !== c.prozoId ? `${c.prozoId} (${c.cpAccountCode})` : c.prozoId })
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label))
  }, [couriers])

  const [courier, setCourier] = useState(presetCourier ? { value: presetCourier, label: presetCourier } : null)
  const [file, setFile] = useState(null)
  const [check, setCheck] = useState(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  // a save changes the merchant's price at once and has no rollback: the confirm needs this tick
  const [understood, setUnderstood] = useState(false)

  const clear = () => { setCheck(null); setError(''); setAcknowledged(false) }
  const replaces = !!courier && existingCouriers.includes(courier.value)

  const run = async (confirm) => {
    setBusy(true)
    setError('')
    try {
      const res = await uploadRateCard({ file, merchantId: merchant.id, courier: courier.value, confirm, acknowledge: confirm && acknowledged })
      if (!confirm) {
        setCheck(res)
      } else {
        setConfirmOpen(false)
        enqueueSnackbar(`${courier.value} rate card saved. The change is in its History.`, { variant: 'success' })
        onSaved()
      }
    } catch (e) {
      setConfirmOpen(false)
      setError(errorText(e, 'Upload failed'))
    } finally {
      setBusy(false)
    }
  }

  const errors = check?.errors || []
  const counts = check?.counts || {}
  const ok = check && errors.length === 0
  const needsAck = ok && check.needsAcknowledge

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onCancel} disabled={busy} sx={{ textTransform: 'none', fontWeight: 600, color: C.inkMuted, mb: 1, px: 0.5 }}>
        Back to rate cards
      </Button>
      <Typography component='h2' sx={TYPE.sectionTitle}>{replaces ? `Replace the ${courier.value} rate card` : 'Upload a rate card'}</Typography>
      <Typography sx={{ ...TYPE.rowHelp, mt: 0.25, mb: 2.5, maxWidth: 760 }}>
        One file per courier. It becomes this merchant&apos;s live {courier ? `${courier.value} ` : ''}card
        {replaces ? ' and replaces the one there now' : ''}. The file is checked first; nothing is saved until you confirm.
      </Typography>

      <Box sx={{ display: 'grid', gap: 2.5, maxWidth: 760 }}>
        <Box>
          <Typography component='label' htmlFor='ptl-rate-card-courier' sx={{ ...TYPE.rowLabel, display: 'block', mb: 0.75 }}>1. Courier</Typography>
          <Autocomplete id='ptl-rate-card-courier' size='small' options={options} loading={isLoading} value={courier}
            onChange={(_, v) => { setCourier(v); clear() }} isOptionEqualToValue={(o, v) => o.value === v.value}
            sx={{ maxWidth: 420 }} disabled={busy}
            renderInput={(p) => <TextField {...p} placeholder='Pick a PTL courier account' />} />
          <Typography sx={{ ...TYPE.rowHelp, mt: 0.5 }}>
            {replaces ? 'This merchant already has a card for this courier; it will be replaced.' : 'Only PTL (B2B) courier accounts are listed.'}
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ ...TYPE.rowLabel, mb: 0.75 }}>2. Rate card file</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <Button component='label' variant='outlined' startIcon={<UploadFileOutlinedIcon />} disabled={busy} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
              {file ? file.name : 'Choose CSV file'}
              <input hidden type='file' accept='.csv' onChange={(e) => { setFile(e.target.files?.[0] || null); clear(); e.target.value = '' }} />
            </Button>
            <Link component='button' type='button' onClick={onTemplate} sx={{ fontSize: 13 }}>Download template</Link>
          </Box>
          <Typography sx={{ ...TYPE.rowHelp, mt: 0.5 }}>
            CSV from the template: one row per lane and weight slab, lanes from the {num(standardLanes)} standard lanes. B2B MIN / MAX TAT columns are not needed; TAT comes from Step 2.
          </Typography>
        </Box>
      </Box>

      {error && <Box sx={{ mt: 2.5 }}><Banner tone='danger'>{error}</Banner></Box>}

      {check && (
        <Box sx={{ mt: 3 }} aria-live='polite'>
          <Typography component='h3' sx={{ ...TYPE.sectionTitle, mb: 1.25 }}>
            {ok ? 'What this upload will save' : 'The file was rejected. Nothing was saved.'}
          </Typography>
          <KpiStrip ariaLabel='Upload check' items={[
            { key: 'rows', label: 'Rows in file', value: num(check.rowsInFile), sub: 'Lane and weight slab rows' },
            { key: 'lanes', label: 'Lanes priced', value: `${num(counts.lanes)} / ${num(counts.standardLanes)}`, tone: counts.missingLanes ? 'warn' : 'ok', sub: 'Standard lanes in this file' },
            { key: 'missing', label: 'Lanes with no price', value: num(counts.missingLanes), tone: counts.missingLanes ? 'warn' : 'ok', sub: counts.missingLanes ? `No ${courier.value} price there` : 'Every standard lane' },
            { key: 'errors', label: 'Rows with errors', value: num(errors.length), tone: errors.length ? 'danger' : 'ok', sub: errors.length ? 'Fix them and check again' : 'File is valid' },
          ]} />

          <UploadErrors errors={errors} keyLabel='Lane' fileName={`ptl_rate_card_${courier?.value || 'upload'}_errors.csv`} />

          {ok && (check.warnings || []).map((w) => <Banner key={w} tone={needsAck ? 'warn' : 'info'}>{w}</Banner>)}
          {needsAck && (
            <FormControlLabel sx={{ mt: -0.5, mb: 1 }}
              control={<Checkbox size='small' checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 13, color: C.inkBody }}>I know some lanes will have no {courier.value} price, and want to save it anyway</Typography>} />
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1, mt: 3, pt: 2, borderTop: `1px solid ${C.line}` }}>
        <Button onClick={onCancel} disabled={busy} sx={{ textTransform: 'none', fontWeight: 600, color: C.inkMuted }}>Cancel</Button>
        {!ok ? (
          <LoadingButton variant='contained' loading={busy} disabled={!file || !courier} onClick={() => run(false)} sx={{ ...SX.cta, minHeight: 36 }}>
            {check ? 'Check again' : 'Check file'}
          </LoadingButton>
        ) : (
          <Button variant='contained' disabled={busy || (needsAck && !acknowledged)} onClick={() => { setUnderstood(false); setConfirmOpen(true) }} sx={{ ...SX.cta, minHeight: 36 }}>
            Save rate card
          </Button>
        )}
      </Box>

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>Save the {courier?.value} rate card?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...TYPE.rowHelp, fontSize: 13.5, color: C.inkBody }}>
            It becomes the live {courier?.value} card of {merchant.name}{replaces ? ', replacing the current one' : ''}. New PTL orders are priced from it
            at once ({num(counts.lanes)} of {num(counts.standardLanes)} lanes); existing shipments keep their price.
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <Banner tone='warn'>This changes pricing at once and cannot be rolled back: to undo it, upload the corrected card.</Banner>
          </Box>
          <FormControlLabel sx={{ mt: 0.5 }}
            control={<Checkbox size='small' checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 13, color: C.inkBody }}>I understand this changes pricing and cannot be rolled back</Typography>} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
          <LoadingButton variant='contained' loading={busy} disabled={!understood} onClick={() => run(true)} sx={SX.cta}>Save rate card</LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
